/*
 * PinReset.tsx
 *
 * Page that prompts user to identify themselves via security questions in order to reset PIN.
 *
 * Created by Nick Perry on 9/2/22.
 *
 * dha-login
 *
 * Copyright © 2009-2022 United States Government as represented by
 * the Program Manager of the DHA: Web and Mobile Technology Program Management Office.
 * All Rights Reserved.
 *
 * Copyright © 2009-2022 Contributors. All Rights Reserved.
 *
 * THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 * REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 * COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 * AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 * THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 * INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 * REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 * DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 * HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 * RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 * Government Agency: DHA: Web and Mobile Technology Program Management Office
 * Government Agency Original Software Designation: dha-login
 * Government Agency Original Software Title: dha-login
 * User Registration Requested. Please send email
 * with your contact information to: robert.a.kayl.civ@mail.mil
 * Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 */

import type { Dispatch, SetStateAction } from 'react';
import { useContext, useEffect, useState } from 'react';

import { Dialog } from '@headlessui/react';
import { decrypt } from 'dha-encryption';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import * as yup from 'yup';

import { themeAtom } from '../../atoms/themeAtom';
import { CustomInput } from '../../components/CustomInput';
import type { IPinResetStatusState } from '../../controllers/LoginController';
import { LoginDatabaseContext } from '../../providers/LoginDatabaseProvider';

// Dispatch and SetStateAction are types from react that are necessary for useState's functional update syntax, aka setState((prevstate) => {})
interface IPinResetProps {
  setPinResetStatus: Dispatch<SetStateAction<IPinResetStatusState>>;
}

const PinReset = ({ setPinResetStatus }: IPinResetProps) => {
  const theme = useAtomValue(themeAtom);
  const [question1, setQuestion1] = useState('');
  const [question2, setQuestion2] = useState('');

  const { DHA_Key, database } = useContext(LoginDatabaseContext);

  const validationSchema = yup.object({
    answer1: yup.string().required('Please answer your security question').trim(),
    answer2: yup.string().required('Please answer your security question').trim(),
  });

  // Effect that initially retrieves the user's security questions to be displayed on render
  useEffect(() => {
    const getSecurityQuestions = async () => {
      // necessary check due to TypeScript restrictions
      if (database !== null) {
        if (DHA_Key === undefined) {
          return;
        }

        try {
          const encryptedUser = await database.userProfile.get('primaryUser');
          if (encryptedUser !== undefined) {
            const decryptedUserString = await decrypt(encryptedUser.userProfile, DHA_Key);
            const decryptedUser = JSON.parse(decryptedUserString);

            // set security questions to be answered
            setQuestion1(decryptedUser.question1);
            setQuestion2(decryptedUser.question2);
          }
        } catch (err) {
          console.log(err);
        }
      }
    };
    getSecurityQuestions().catch((err) => console.log(err));
  }, [DHA_Key, database]);

  return (
    <Dialog
      open={true}
      as="div"
      className="relative z-10"
      onClose={(boolie) => {
        if (boolie) console.log(''); // this is the peak of programming & human ingenuity.
      }}
    >
      <div className={`fixed inset-0 ${theme.modalBackground}`} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Dialog.Panel
            className={`h-full max-h-fit w-full overflow-hidden rounded-2xl ${theme.cardColor} p-6 text-center align-middle shadow-xl transition-allmd:min-w-[25%] md:max-w-fit`}
          >
            <Dialog.Title as="h3" className={`text-lg ffont-medium leading-6 ${theme.textColor}`}>
              Reset PIN
            </Dialog.Title>
            <div className="mt-2">
              <p className={`text-sm ${theme.textColor}`}>
                Answer the following questions to reset your PIN
              </p>
            </div>

            <Formik
              initialValues={{
                answer1: '',
                answer2: '',
              }}
              onSubmit={(enteredValues, { setErrors }) => {
                if (database !== null) {
                  const verifyPin = async () => {
                    if (DHA_Key === undefined) {
                      return;
                    }

                    try {
                      const encryptedUser = await database.userProfile.get('primaryUser');
                      if (encryptedUser !== undefined) {
                        const decryptedValues = await decrypt(encryptedUser.userProfile, DHA_Key);
                        const decryptedUser = JSON.parse(decryptedValues);

                        if (
                          decryptedUser.answer1.toLowerCase() ===
                            enteredValues.answer1.toLowerCase() &&
                          decryptedUser.answer2.toLowerCase() ===
                            enteredValues.answer2.toLowerCase()
                        ) {
                          setPinResetStatus((prevState: IPinResetStatusState) => {
                            return {
                              ...prevState,
                              answeringSecurityQuestions: false,
                              makingNewPin: true,
                            };
                          });
                        } else {
                          setErrors({
                            answer1: 'Please answer both questions correctly.',
                            answer2: 'Please answer both questions correctly.',
                          });
                        }
                      }
                    } catch (err) {
                      console.log(err);
                    }
                  };
                  verifyPin().catch((e) => console.error(e));
                }
              }}
              validationSchema={validationSchema}
            >
              {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit}>
                  <div id="form-area" className="">
                    <div className="mt-8">
                      {/* headlessUI Listbox component */}
                      <div className={`text-left font-semibold ${theme.textColor}`}>
                        {question1}
                      </div>

                      <CustomInput
                        type="text"
                        name="answer1"
                        value={formikProps.values.answer1}
                        tailwindClasses="mt-2"
                        isError={!!formikProps.errors.answer1}
                        isTouched={formikProps.touched.answer1}
                        onChange={formikProps.handleChange}
                        placeholder="answer here..."
                      />

                      {/* error message shows up on submit attempt if input is invalid */}
                      {formikProps.errors.answer1 && formikProps.touched.answer1 ? (
                        <div className="text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.answer1}
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-8">
                      {/* headlessUI Listbox component */}
                      <div className={`text-left font-semibold ${theme.textColor}`}>
                        {question2}
                      </div>

                      <CustomInput
                        type="text"
                        name="answer2"
                        value={formikProps.values.answer2}
                        tailwindClasses="mt-2"
                        isError={!!formikProps.errors.answer2}
                        isTouched={formikProps.touched.answer2}
                        onChange={formikProps.handleChange}
                        placeholder="answer here..."
                      />

                      {/* error message shows up on submit attempt if input is invalid */}
                      {formikProps.errors.answer2 && formikProps.touched.answer2 ? (
                        <div className="text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.answer2}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="">
                    <button
                      type="submit"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium ${theme.buttonShadow} ${theme.buttonColor} ${theme.buttonTextColor} hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 mt-5`}
                    >
                      Submit
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default PinReset;
